import React, { useMemo } from 'react';

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import styled from 'styled-components';

import { Paper, Typography } from '@mui/material';

import {
  IRoomAgenda as RoomAgendaType,
  RoomAgendaEvent,
  OccurrenceStatus,
  EventOccurrence,
} from './types/roomAgenda';
import Text from 'components/Text';

interface RoomAgendaProps {
  roomAgenda: RoomAgendaType;
  onSelectOccurrence?: (args: {
    event: RoomAgendaEvent;
    occurrenceKey: string;
    status: OccurrenceStatus;
  }) => void;
  selectedOccurrenceKey?: string | null;
}

dayjs.extend(utc);
dayjs.extend(timezone);

const SectionTitle = styled.div`
  padding: 4px 8px 12px 8px;
`;

const Card = styled.button<{ $active?: boolean; $selected?: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  background: ${({ theme }) => theme.COLORS.BACKGROUND_PRIMARY};
  border: 1px solid
    ${({ theme, $selected }) => ($selected ? theme.COLORS.PRIMARY : theme.COLORS.GRAY_80)};
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 10px;
  color: ${({ theme }) => theme.COLORS.TEXT_PRIMARY};
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatusDot = styled.span<{ color: string }>`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ color }) => color};
`;

const DetailsLink = styled.span`
  display: inline-block;
  color: ${({ theme }) => theme.primary};
`;

function formatDuration(minutes: number) {
  if (!minutes || Number.isNaN(minutes)) return '';
  if (minutes % 60 === 0) return `${Math.floor(minutes / 60)} horas`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins} minutos`;
  return `${hours}h ${mins}min`;
}

const RoomAgenda: React.FC<RoomAgendaProps> = ({
  roomAgenda,
  onSelectOccurrence,
  selectedOccurrenceKey,
}) => {
  const occurrences = useMemo<EventOccurrence[]>(() => {
    const nowUtc = dayjs.utc();
    const list: EventOccurrence[] = [];

    // Use schedules as the source of truth for dates/times
    for (const schedule of roomAgenda.schedules || []) {
      const ev = roomAgenda.events.find(e => e.id === schedule.event_id);
      if (!ev) continue;

      // Parse as UTC then convert to local for display
      const startUtc = dayjs.utc(`${schedule.init_date}T${schedule.init_time}`);
      const endUtc = dayjs.utc(`${schedule.end_date}T${schedule.end_time}`);
      const start = startUtc.local();
      const end = endUtc.local();
      const duration = endUtc.diff(startUtc, 'minute');

      let status: OccurrenceStatus = 'UPCOMING';
      if (nowUtc.isAfter(endUtc)) status = 'FINISHED';
      else if (nowUtc.isAfter(startUtc) && nowUtc.isBefore(endUtc)) {
        status = 'ONGOING';
        onSelectOccurrence?.({
          event: ev,
          occurrenceKey: `${ev.id}-${startUtc.toISOString()}`,
          status,
        });
      }

      list.push({
        eventId: ev.id,
        title: ev.name,
        start,
        end,
        durationMinutes: duration,
        status,
        isOnline: !!ev.local?.is_online,
        event: ev,
        startUtcIso: startUtc.toISOString(),
      });
    }

    return list.sort((a, b) => a.start.valueOf() - b.start.valueOf()).slice(0, 50); // defensive limit
  }, [roomAgenda]);

  const upcoming = useMemo(() => occurrences.filter(o => o.status !== 'FINISHED'), [occurrences]);
  const previous = useMemo(() => occurrences.filter(o => o.status === 'FINISHED'), [occurrences]);

  const getStatus = (o: EventOccurrence) => {
    switch (o.status) {
      case 'ONGOING':
        return { label: 'Acontecendo', color: '#00E23F' };
      case 'UPCOMING':
        return { label: 'Aguardando', color: '#FFC000' };
      default:
        return { label: 'Finalizada', color: '#D9D9D9' };
    }
  };

  return (
    <Paper sx={{ width: '100%' }}>
      <SectionTitle>
        <Text size="medium" bold>
          Próximos Eventos
        </Text>
      </SectionTitle>

      {upcoming.length === 0 && (
        <SectionTitle>
          <Typography variant="caption">Nenhum evento registado nesta sala.</Typography>
        </SectionTitle>
      )}
      {upcoming.map(o => {
        const status = getStatus(o);
        return (
          <Card
            key={`${o.eventId}-${o.start.toISOString()}`}
            $active={o.status === 'ONGOING'}
            $selected={selectedOccurrenceKey === `${o.eventId}-${o.startUtcIso}`}
          >
            <Text size="small" bold truncate tooltip={o.title}>
              {o.title}
            </Text>
            <Text size="small">
              {o.start.format('DD/MM/YYYY')} às {o.start.format('HH:mm')}
            </Text>
            <Text size="small">Duração: {formatDuration(o.durationMinutes)}</Text>
            <Row>
              <StatusDot color={status.color} />
              <Text size="small">{status.label}</Text>
            </Row>
            <Row style={{ justifyContent: 'flex-end' }}>
              <DetailsLink
                onClick={() =>
                  onSelectOccurrence?.({
                    event: o.event,
                    occurrenceKey: `${o.eventId}-${o.startUtcIso}`,
                    status: o.status,
                  })
                }
              >
                <Text size="small">Detalhes da reunião</Text>
              </DetailsLink>
            </Row>
          </Card>
        );
      })}

      {previous.length > 0 && (
        <SectionTitle>
          <Text size="medium" bold>
            Reuniões anteriores
          </Text>
        </SectionTitle>
      )}

      {previous.map(o => {
        const status = getStatus(o);
        return (
          <Card
            key={`${o.eventId}-${o.start.toISOString()}-prev`}
            $selected={selectedOccurrenceKey === `${o.eventId}-${o.startUtcIso}`}
          >
            <Text size="small" bold truncate tooltip={o.title}>
              {o.title}
            </Text>
            <Text size="small">
              {o.start.format('DD/MM/YYYY')} às {o.start.format('HH:mm')}
            </Text>
            <Text size="small">Duração: {formatDuration(o.durationMinutes)}</Text>
            <Row>
              <StatusDot color={status.color} />
              <Text size="small">{status.label}</Text>
            </Row>
          </Card>
        );
      })}
    </Paper>
  );
};

export default RoomAgenda;
